import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';

import {useAppUpdateBanner} from './useAppUpdateBanner';

/**
 * The props for the `AppUpdateBanner` component.
 */
interface ComponentProps {
    /**
     * The `testId` property represents a unique identifier assigned to a component for testing purposes.
     * It is applied as `data-cy` attribute to enable reliable targeting in Cypress tests.
     *
     * @default 'AppUpdateBanner'
     */
    testId?: string;
}

/**
 * A fixed-position banner shown when a new service worker version is waiting.
 * Prompts the user to activate the update and reload.
 *
 * @param props        The component props.
 * @param props.testId An optional test ID used for automation selectors. Defaults to `'AppUpdateBanner'`.
 * @returns A JSX element rendering the AppUpdateBanner, or null if no update is pending.
 *
 * @example
 * ```tsx
 * <AppUpdateBanner />
 * ```
 */
const AppUpdateBanner = ({testId = 'AppUpdateBanner'}: ComponentProps) => {
    const {applyUpdate, hasUpdate, t} = useAppUpdateBanner();

    if (!hasUpdate) return null;

    return (
        <Banner data-cy={testId}>
            <BannerText>{t('common.updateAvailable')}</BannerText>
            <UpdateButton data-cy={`${testId}-apply`} type="button" onClick={applyUpdate}>
                {t('common.updateNow')}
            </UpdateButton>
        </Banner>
    );
};

AppUpdateBanner.displayName = 'AppUpdateBanner';

export {AppUpdateBanner};

const Banner = styled.div`
    align-items: center;
    background: ${({theme}) => theme.colors.primaryDefault};
    border-radius: 1.2rem;
    bottom: ${spacing(4)};
    box-shadow: ${({theme}) => theme.boxShadows.card};
    display: flex;
    justify-content: space-between;
    left: ${spacing(4)};
    padding: ${spacing(3)} ${spacing(4)};
    position: fixed;
    right: ${spacing(4)};
    z-index: 9999;
`;

const BannerText = styled.span`
    color: ${({theme}) => theme.colors.white};
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.4rem;
    font-weight: 500;
`;

const UpdateButton = styled.button`
    background: ${({theme}) => theme.colors.accentWarm};
    border: none;
    border-radius: 0.8rem;
    color: ${({theme}) => theme.colors.white};
    cursor: pointer;
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.3rem;
    font-weight: 600;
    padding: ${spacing(2)} ${spacing(4)};
    white-space: nowrap;

    &:hover {
        opacity: 0.9;
    }
`;